import * as angular from 'angular';
import * as _ from 'lodash';
import nvSharedModelsModule from './nv.shared.models.module';

angular
  .module(nvSharedModelsModule)

  .factory('NvBaseModel', function($resource, PublicConfig, UtilitiesService, UserRolePermissionsForModelService) {
    const defaultParamDefaults = {
      id: '@_id',
    };

    let defaultActions = {
      get: { method: 'GET' },
      query: {
        method: 'GET',
        isArray: true,
        transformResponse(data) {
          let json = angular.fromJson(data);
          // if the request is cancelled, json will be null
          if (json) {
            // If endpoint uses pagination, json will look like {data: [items]},
            // Otherwise it will just look like [items]
            return json.data || json;
          }
        },
        cancellable: true,
      },
      create: {
        method: 'POST',
        params: { id: null },
      },
      patch: {
        method: 'PATCH',
        transformResponse(data: string) {
          let json: any = angular.fromJson(data);

          if (json) return json.data || json;
        },
      },
      _where: {
        method: 'GET',
        isArray: true,
        transformResponse(data) {
          const json = angular.fromJson(data);
          // if the request is cancelled, json will be null
          // if endpoint supports pagination, will be {data: []}
          // otherwise will be []
          if (json) return json.data || json;
        },
        params: {
          where: '@where',
        },
      },
    };

    // nvUrlPart should be something like '/v1/users/:id'
    // The rest of these are the same as $resource
    let NvBaseModel = function(
      nvUrlPart,
      paramDefaults,
      actions,
      options,
      PERMISSIONING_MODEL_NAME,
      relationToUserPath,
    ) {
      paramDefaults = angular.extend({}, defaultParamDefaults, paramDefaults);
      actions = angular.merge({}, defaultActions, actions);

      actions.create.url = PublicConfig.NV_API_ORIGIN + nvUrlPart.replace(/\/:id$/, '');

      let NewModel = $resource(PublicConfig.NV_API_ORIGIN + nvUrlPart, paramDefaults, actions, options);

      angular.extend(NewModel.prototype, {
        toPOJO() {
          let self = this;
          return JSON.parse(
            JSON.stringify(self, function(key, value) {
              let val = value;
              if (key !== '_id' && angular.isString(key) && key.charAt(0) === '_') {
                val = undefined;
              }
              return val;
            }),
          );
        },

        // Deletes all props except functions and props that start with a '$'. Also sets _id to null.
        _clear() {
          let self = this;
          _.forEach(self, function(value, key: string) {
            let deleteProp = true;
            if (typeof value === 'function') deleteProp = false;
            if (_.startsWith(key, '$')) deleteProp = false;
            if (deleteProp) {
              delete self[key];
            }
          });
          self._id = null;
        },

        // Gets a sub-object from the model by a "dot notation" path.
        // Example:
        // var obj = new MyModel({ profile: { name: { first: "Bob", last: "Smith" } } });
        // obj.getFieldByPath('name.firstLast');
        // // -> // name = Bob
        getFieldByPath(path) {
          return UtilitiesService.getFieldByPath(this, path);
        },

        setFieldByPath(path, newValue) {
          return UtilitiesService.setFieldByPath(this, path, newValue);
        },

        // returns a reference of the next to last key in a path that uses "dot notation".
        // this is useful in combination with student#getLastKeyInPath to create a
        // reference to a property when using    ng-model
        // @param path {String} is the "dot notation" path of a property in the instance object
        // Example:
        // var path = "studentDetails.testingModifications.codeString"
        // @returns {Object} { codeString: valueOfCodeString }, which is a reference to that object in the instance
        getReferenceOfNextToLastKeyInPath(path) {
          let self = this;
          let paths = path.split('.');
          let reference = self[paths[0]];

          paths.pop();
          for (let i = 1; i < paths.length; ++i) {
            if (_.isUndefined(reference)) {
              return undefined;
            } else {
              reference = reference[paths[i]];
            }
          }
          return reference;
        },

        // @param path {String} is the "dot notation" path of a property in the instance object
        // Example:
        // var path = "studentDetails.testingModifications.codeString"
        // @returns {String} "codeString"
        getLastKeyInPath(path) {
          return path.split('.').pop();
        },

        // fields is an array of "dot notation" paths of the fields that should be patched
        generatePatch(fields) {
          return UtilitiesService.generatePatch(this, fields);
        },

        $patchFields(fields, success, error) {
          let self = this;
          let patch = self.generatePatch(fields);
          let result = NewModel.patch.call(this, { id: self._id }, patch, success, error);
          return result.$promise || result;
        },

        applyPatch(patch) {
          UtilitiesService.patchObject(this, patch);
          return this;
        },
      });

      if (PERMISSIONING_MODEL_NAME) {
        const canCreateFn = UserRolePermissionsForModelService.canCreatePartial(
          PERMISSIONING_MODEL_NAME,
          relationToUserPath,
        );
        const canDeleteFn = UserRolePermissionsForModelService.canDeletePartial(
          PERMISSIONING_MODEL_NAME,
          relationToUserPath,
        );
        const canEditFn = UserRolePermissionsForModelService.canEditPartial(
          PERMISSIONING_MODEL_NAME,
          relationToUserPath,
        );
        const canViewFn = UserRolePermissionsForModelService.canViewPartial(
          PERMISSIONING_MODEL_NAME,
          relationToUserPath,
        );

        angular.extend(NewModel.prototype, {
          canCreate(viewingUser) {
            return canCreateFn(this, viewingUser);
          },

          canDelete(viewingUser) {
            return canDeleteFn(this, viewingUser);
          },

          canEdit(viewingUser) {
            return canEditFn(this, viewingUser);
          },

          canView(viewingUser) {
            return canViewFn(this, viewingUser);
          },
        });
      }

      NewModel.extend = function(props) {
        angular.extend(NewModel.prototype, props);
      };

      /**
       * Creates a "where" query, which must be stringified
       */
      NewModel.where = function(where) {
        return NewModel._where({
          where: JSON.stringify(where),
        });
      };

      return NewModel;
    };

    // Sets a sub-object by a path.
    //
    // Note: this is a static method since it is just used within generatePatch, but it is complex enough to test
    // separately.
    //
    // Example:
    // var obj = { profile: { name: { first: "Bob", last: "Smith" } } };
    // NvBaseModel.setFieldByPath(obj, 'profile.name', { fullName: "Bob Smith" });
    // obj;
    // // -> { profile: { name: { fullName: "Bob Smith" } } }
    const _setFieldByPath = function(obj, path, value) {
      return UtilitiesService.setFieldByPath(obj, path, value);
    };
    _.extend(NvBaseModel, { _setFieldByPath });

    return NvBaseModel;
  });
